<template>
    <v-container class="article">
        <v-row v-if="!article" justify="center">
            <v-col cols="12" class="article-column">
                <v-skeleton-loader type="image, article" />
            </v-col>
        </v-row>

        <template v-else>
            <v-row justify="center">
                <v-col cols="12" class="article-column pb-0">
                    <v-btn small depressed color="white" class="mb-3 back-btn" :to="{ name: 'News' }">
                        <v-icon small left>mdi-arrow-left</v-icon>
                        Ολα τα νεα
                    </v-btn>
                </v-col>
            </v-row>

            <v-row justify="center">
                <v-col cols="12" class="article-hero-column pt-0">
                    <div class="article-hero">
                        <div class="media-blur-backdrop" :style="{ backgroundImage: `url('${getImage()}')` }" />
                        <v-img :src="getImage()" contain aspect-ratio="1.7778" class="article-hero__img">
                            <div class="article-hero__shade" />
                            <div class="article-hero__lower-third">
                                <div class="article-hero__tag custom-orange">
                                    <span>ΝΕΑ</span>
                                    <span v-if="article.date" class="article-hero__date">{{ getDate() }}</span>
                                </div>
                            </div>
                        </v-img>
                        <div class="article-hero__led" />
                    </div>
                </v-col>
            </v-row>

            <v-row justify="center">
                <v-col cols="12" class="article-column">
                    <div class="article-body">
                        <h1 class="article-title">{{ article.title }}</h1>
                        <div class="article-meta">
                            <span v-if="article.date">
                                <v-icon small>mdi-clock-outline</v-icon>
                                {{ getDate() }}
                            </span>
                            <v-spacer />
                            <v-btn icon small title="Κοινοποίηση" @click="share">
                                <v-icon small>{{ copied ? 'mdi-check' : 'mdi-share-variant' }}</v-icon>
                            </v-btn>
                        </div>
                        <div class="article-divider custom-orange" />
                        <div class="article-content" v-html="article.content" />
                    </div>
                </v-col>
            </v-row>

            <v-row v-if="moreArticles.length" justify="center" class="mt-6">
                <v-col cols="12" class="article-more-column">
                    <h2 class="more-title">
                        <span class="more-title__bar custom-orange" />
                        Περισσοτερα νεα
                    </h2>
                    <v-row>
                        <v-col v-for="item in moreArticles" :key="item.id" cols="12" sm="6" md="4">
                            <article-card :article="item" />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import ArticleCard from '@/components/ArticleCard'
    import articleService from '@/services/article'
    export default {
        components: { ArticleCard },
        data() {
            return {
                article: null,
                moreArticles: [],
                copied: false
            }
        },
        watch: {
            '$route.params.id': {
                handler() {
                    this.getArticle()
                }
            }
        },
        created() {
            this.getArticle()
        },
        methods: {
            getImage() {
                return `${'https://storage.googleapis.com'}/${this.article.image}`
            },
            getDate() {
                return momentTimezone(this.article.date).tz('Europe/Athens').format('DD/MM/YYYY, HH:mm')
            },
            getArticle() {
                this.article = null
                window.scrollTo(0, 0)
                articleService.getArticle(this.$route.params.id).then(resp => {
                    this.article = resp.data
                })
                this.getMoreArticles()
            },
            getMoreArticles() {
                articleService.getArticles(1, 4).then(resp => {
                    this.moreArticles = resp.data.content
                        .filter(item => String(item.id) !== String(this.$route.params.id))
                        .slice(0, 3)
                })
            },
            share() {
                const url = window.location.href
                if (navigator.share) {
                    navigator.share({ title: this.article.title, url }).catch(() => {})
                } else if (navigator.clipboard) {
                    navigator.clipboard.writeText(url).then(() => {
                        this.copied = true
                        setTimeout(() => { this.copied = false }, 2000)
                    })
                }
            }
        }
    }
</script>

<style scoped>
.article-column {
    max-width: 780px;
}

.article-hero-column {
    max-width: 1000px;
}

.article-more-column {
    max-width: 1100px;
}

.back-btn {
    letter-spacing: 0.06em;
    font-weight: 700;
}

/* ---------- Hero ---------- */
.article-hero {
    position: relative;
    border: 6px solid #0d0d0f;
    border-radius: 10px;
    overflow: hidden;
    background: #000;
    box-shadow: 0 24px 40px -20px rgba(239, 68, 68, 0.45);
}

.article-hero__shade {
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 0.6) 100%);
}

.article-hero__lower-third {
    position: absolute;
    left: 20px;
    bottom: 18px;
}

.article-hero__tag {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 4px 22px 4px 12px;
    color: white;
    font-weight: 800;
    font-size: 0.8rem;
    letter-spacing: 0.12em;
    clip-path: polygon(0 0, 100% 0, calc(100% - 10px) 100%, 0 100%);
}

.article-hero__date {
    font-weight: 500;
    opacity: 0.9;
}

.article-hero__led {
    position: absolute;
    z-index: 2;
    inset: 0;
    pointer-events: none;
    background-image: radial-gradient(rgba(0, 0, 0, 0.55) 35%, transparent 36%);
    background-size: 3px 3px;
    opacity: 0.25;
}

/* ---------- Body ---------- */
.article-body {
    background: white;
    border-radius: 12px;
    padding: 28px 36px 36px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
}

.article-title {
    font-size: 2.2rem;
    line-height: 1.2;
    font-weight: 800;
    text-align: left;
    word-break: break-word;
}

.article-meta {
    display: flex;
    align-items: center;
    margin-top: 12px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.9rem;
}

.article-divider {
    height: 4px;
    width: 64px;
    border-radius: 2px;
    margin: 16px 0 24px;
}

/* ---------- More ---------- */
.more-title {
    display: flex;
    align-items: center;
    gap: 10px;
    width: fit-content;
    padding: 6px 16px 6px 10px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
    font-size: 1.3rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 4px;
}

.more-title__bar {
    width: 6px;
    height: 24px;
    border-radius: 2px;
}

@media (max-width: 600px) {
    .article-hero {
        border-width: 3px;
    }

    .article-body {
        padding: 20px 16px 24px;
    }

    .article-title {
        font-size: 1.5rem;
    }
}
</style>

<style>
    .article .article-content {
        width: 100%;
        max-width: 100%;
        font-family: Ubuntu, Arial, sans-serif !important;
        font-size: 18px;
        line-height: 1.75;
        text-align: left;
        overflow-wrap: anywhere;
        word-break: break-word;
    }

    .article .article-content p {
        margin-bottom: 1.2em;
    }

    .article .article-content h2,
    .article .article-content h3 {
        margin: 1.6em 0 0.6em;
        line-height: 1.3;
        font-weight: 700;
    }

    .article .article-content a {
        color: #ef4444;
        font-weight: 600;
    }

    .article .article-content blockquote {
        margin: 1.5em 0;
        padding: 0.5em 1.2em;
        border-left: 4px solid #ef4444;
        background: rgba(239, 68, 68, 0.06);
        font-style: italic;
    }

    .article .article-content img,
    .article .article-content video,
    .article .article-content iframe,
    .article .article-content canvas,
    .article .article-content svg {
        max-width: 100%;
        height: auto;
    }

    .article .article-content img {
        border-radius: 8px;
    }

    .article .article-content table {
        display: block;
        width: 100%;
        max-width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .article .article-content pre,
    .article .article-content code {
        max-width: 100%;
        white-space: pre-wrap;
        word-break: break-word;
    }

    @media (max-width: 600px) {
        .article .article-content {
            font-size: 16px;
        }
    }
</style>
